'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import type { BrandImageFragmentFragmentDoc } from '@packages/gql/generated/shopping/BrandImageFragmentFragmentDoc';
import React from 'react';

import { unmask } from '@packages/gql/src/betterMasking';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { Link } from '@packages/modules/src/Link/Link';

/* GraphQL */ `
  fragment BrandImageFragment on Product {
    brand {
      name
      image
    }
    productSeo: seo(locale: $locale) {
      brandLink {
        url
      }
    }
  }
`;

export type BrandImageProps = {
  data: FragmentType<typeof BrandImageFragmentFragmentDoc>;
};

export const brandImageSize = 52;

type ImgageCompProps = { src: string; alt: string; onErrorHandler(): void };
const ImageComp = ({ src, alt, onErrorHandler }: ImgageCompProps) => (
  <Image
    src={src}
    alt={alt}
    width={brandImageSize}
    height={brandImageSize}
    className="m-auto block size-auto max-h-[52px] max-w-[52px] object-contain"
    onError={onErrorHandler}
  />
);

/**
 * component to display a product brand image with an optional link if available
 *
 * concept: The brand image should have the dimensions from the image that gets loaded. But it must have a maximum size of 52px x 52px.
 * It should be centered horizontal and vertical.
 */
export const BrandImage = ({ data }: BrandImageProps) => {
  const { brand, productSeo } = unmask<typeof BrandImageFragmentFragmentDoc>(data);

  const [brandImage, setBrandImage] = React.useState(brand?.image || null);

  if (!brand || brandImage === null) return null;

  const imageProps: ImgageCompProps = {
    src: brandImage,
    alt: brand.name,
    onErrorHandler: () => setBrandImage(null),
  };

  return (
    <div className="max-h-[52px] w-[52px]">
      {productSeo?.brandLink?.url ? (
        <Link href={productSeo.brandLink.url}>
          <ImageComp {...imageProps} />
        </Link>
      ) : (
        <ImageComp {...imageProps} />
      )}
    </div>
  );
};
